import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { Box, Button, Stack, Text } from '@ui'
import type {
    MiniappEthTransactionRequest,
    MiniappTransactionMethod,
} from 'hooks/useMiniappEthereumProvider'
import { shortAddress, shortenHexString } from 'ui/utils/utils'

export type MiniappTransactionPrompt = {
    tx: MiniappEthTransactionRequest
    method: MiniappTransactionMethod
}

type Props = {
    prompt: MiniappTransactionPrompt | null
    onApprove: () => void
    onReject: () => void
}

type TransactionPromptResolver = {
    resolve: () => void
    reject: (reason?: unknown) => void
}

export const useMiniappTransactionPrompt = () => {
    const [prompt, setPrompt] = useState<MiniappTransactionPrompt | null>(null)
    const resolverRef = useRef<TransactionPromptResolver | null>(null)

    const requestTransactionApproval = useCallback((details: MiniappTransactionPrompt) => {
        return new Promise<void>((resolve, reject) => {
            // Reject new requests if one is already pending to prevent promise leaks.
            // The caller should wait for the current request to complete.
            if (resolverRef.current) {
                reject(new Error('Another transaction request is pending'))
                return
            }
            resolverRef.current = { resolve, reject }
            setPrompt(details)
        })
    }, [])

    const handleTransactionApprove = useCallback(() => {
        resolverRef.current?.resolve()
        resolverRef.current = null
        setPrompt(null)
    }, [])

    const handleTransactionReject = useCallback((reason?: Error) => {
        resolverRef.current?.reject(reason ?? new Error('User rejected transaction'))
        resolverRef.current = null
        setPrompt(null)
    }, [])

    useEffect(() => {
        return () => {
            if (resolverRef.current) {
                resolverRef.current.reject(new Error('Miniapp closed'))
                resolverRef.current = null
            }
        }
    }, [])

    return {
        prompt,
        requestTransactionApproval,
        handleTransactionApprove,
        handleTransactionReject,
    }
}

const formatHexValue = (value?: string) => {
    if (!value) {
        return '—'
    }
    try {
        return `${BigInt(value)} wei`
    } catch {
        return value
    }
}

const formatChainId = (value?: string) => {
    if (!value) {
        return '—'
    }
    try {
        const asBigInt = BigInt(value)
        if (value.startsWith('0x')) {
            return `${asBigInt.toString()} (${value})`
        }
        return `${value} (0x${asBigInt.toString(16)})`
    } catch {
        return value
    }
}

type TransactionDetailRow = {
    name: string
    value: unknown
    type?: string
}

const buildTransactionDetailRows = (tx: MiniappEthTransactionRequest): TransactionDetailRow[] => {
    return [
        {
            name: 'From',
            value: tx.from ?? 'Unknown address',
            type: tx.from ? 'address' : undefined,
        },
        {
            name: 'To',
            value: tx.to ?? 'Contract creation',
            type: tx.to ? 'address' : undefined,
        },
        {
            name: 'Chain ID',
            value: formatChainId(tx.chainId),
        },
        {
            name: 'Value',
            value: formatHexValue(tx.value),
        },
        {
            name: 'Gas Limit',
            value: formatHexValue(tx.gas),
        },
        {
            name: 'Gas Price',
            value: formatHexValue(tx.gasPrice),
        },
    ]
}

const formatScalarValue = (value: string, type?: string) => {
    if (type === 'address') {
        return shortAddress(value)
    }
    if (value.startsWith('0x')) {
        return shortenHexString(value)
    }
    return value
}

const StructuredValue = ({ value, type }: { value: unknown; type?: string }) => {
    if (
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        const displayValue =
            typeof value === 'string' ? formatScalarValue(value, type) : String(value)
        return <Text>{displayValue}</Text>
    }

    return (
        <Box as="pre" whiteSpace="preWrap">
            {JSON.stringify(value, null, 2)}
        </Box>
    )
}

const TransactionDetailsSection = ({ rows }: { rows: TransactionDetailRow[] }) => {
    if (rows.length === 0) {
        return null
    }

    return (
        <Stack gap background="level1" padding="md" borderRadius="md">
            {rows.map((row) => (
                <Stack
                    horizontal
                    key={row.name}
                    justifyContent="spaceBetween"
                    alignItems="start"
                    gap="md"
                >
                    <Text color="gray2">{row.name}</Text>
                    <Box textAlign="right" style={{ maxWidth: '65%', overflowWrap: 'anywhere' }}>
                        <StructuredValue value={row.value} type={row.type} />
                    </Box>
                </Stack>
            ))}
        </Stack>
    )
}

type MethodCopy = {
    heading: string
    touchTitle: string
    description: string
    ctaLabel: string
}

const getMethodCopy = (method: MiniappTransactionMethod, fromDisplay: string): MethodCopy => {
    switch (method) {
        case 'eth_signTransaction':
            return {
                heading: 'Miniapp wants to sign a transaction',
                touchTitle: 'Sign transaction',
                description: `Signing will produce a signed payload from ${fromDisplay}, but it will not be broadcast.`,
                ctaLabel: 'Confirm & Sign',
            }
        case 'eth_sendRawTransaction':
            return {
                heading: 'Miniapp wants to send a raw transaction',
                touchTitle: 'Send raw transaction',
                description: `The raw transaction will be broadcast exactly as provided from ${fromDisplay}.`,
                ctaLabel: 'Confirm & Send',
            }
        case 'eth_sendTransaction':
            return {
                heading: 'Miniapp wants to send a transaction',
                touchTitle: 'Send transaction',
                description: `Once confirmed, it will be sent on-chain from ${fromDisplay}.`,
                ctaLabel: 'Confirm & Send',
            }
        default:
            throw new Error(`Unsupported method: ${method}`)
    }
}

export const MiniappTransactionModal = ({ prompt, onApprove, onReject }: Props) => {
    const detailRows = useMemo(() => {
        if (!prompt) {
            return []
        }
        return buildTransactionDetailRows(prompt.tx)
    }, [prompt])

    if (!prompt) {
        return null
    }

    const calldata = prompt.tx.data
    const fromDisplay = prompt.tx.from ? shortAddress(prompt.tx.from) : 'Unknown address'
    const methodCopy = getMethodCopy(prompt.method, fromDisplay)

    return (
        <ModalContainer
            disableBackgroundClick
            touchTitle={methodCopy.touchTitle}
            padding="lg"
            onHide={onReject}
        >
            <Stack gap="lg">
                <Stack gap="sm">
                    <Text fontWeight="strong">{methodCopy.heading}</Text>
                    <Stack gap="xs">
                        <Text color="gray2" fontSize="sm">
                            {methodCopy.description}
                        </Text>
                        <Text color="gray2" fontSize="xs">
                            RPC method: {prompt.method}
                        </Text>
                    </Stack>
                </Stack>

                <Stack gap>
                    <Text fontWeight="medium">Transaction Details</Text>
                    <TransactionDetailsSection rows={detailRows} />
                </Stack>

                {typeof calldata === 'string' && (
                    <Stack gap background="level1" padding="md" borderRadius="md">
                        <Text fontWeight="medium">Calldata</Text>
                        <Box as="pre" whiteSpace="preWrap">
                            {calldata || '0x'}
                        </Box>
                    </Stack>
                )}

                <Stack gap horizontal justifyContent="end">
                    <Button tone="transparent5" onClick={onReject}>
                        Cancel
                    </Button>
                    <Button tone="cta1" onClick={onApprove}>
                        {methodCopy.ctaLabel}
                    </Button>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}
